import React, { lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './hooks/useAuth';
import { TenantProvider } from './hooks/useTenantContext';
import LoginPage from './pages/LoginPage';
import TenantPickerPage from './pages/TenantPickerPage';
import BetaAccessPage from './pages/BetaAccessPage';
import { APP_TILES } from './config/appCatalog';
import './index.css';
import { useTheme } from './hooks/useTheme';
import { Spinner } from './components/Spinner';
import WorkbookReviewNotifications from './components/WorkbookReviewNotifications';

/** Small initial routes stay eager; heavy app surfaces load in separate chunks (<500kB each). */
const PortalPage = lazy(() => import('./pages/PortalPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const CreateProjectPage = lazy(() => import('./pages/CreateProjectPage'));
const FineTuneDashboard = lazy(() => import('./pages/FineTuneDashboard'));
const PMPage = lazy(() => import('./pages/PMPage'));
const ProfilePage = lazy(() => import('./pages/Profile'));
const StandardsStudio = lazy(() => import('./pages/StandardsStudio'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const DeliverablesAdmin = lazy(() => import('./pages/DeliverablesAdmin'));
const ModelContextWindows = lazy(() => import('./pages/ModelContextWindows'));
const UserAdminPage = lazy(() => import('./pages/UserAdminPage'));
const OperatorUserAdminPage = lazy(() =>
  import('./pages/UserAdminPage').then((m) => ({
    default: m.OperatorUserAdminPage,
  }))
);
const OrganizationsAdminPage = lazy(
  () => import('./pages/OrganizationsAdminPage')
);
const PlatformTenantsPage = lazy(() => import('./pages/PlatformTenantsPage'));
const OperatorConsoleLayout = lazy(
  () => import('./pages/OperatorConsoleLayout')
);
const OperatorConsoleHome = lazy(() => import('./pages/OperatorConsoleHome'));
const OperatorEphemeralEnvironmentsPage = lazy(
  () => import('./pages/OperatorEphemeralEnvironmentsPage')
);
const CompanyDocumentsPage = lazy(() => import('./pages/CompanyDocumentsPage'));
const SpreadsheetEditorPage = lazy(
  () => import('./pages/SpreadsheetEditorPage')
);
const WorkbookPlanReviewPage = lazy(
  () => import('./pages/WorkbookPlanReviewPage')
);
const SpreadsheetPortalBootstrap = lazy(
  () => import('./pages/SpreadsheetPortalBootstrap')
);
const DocumentEditorPage = lazy(() => import('./pages/DocumentEditorPage'));
const DocumentPortalBootstrap = lazy(
  () => import('./pages/DocumentPortalBootstrap')
);
const CadEditorPage = lazy(() => import('./pages/CadEditorPage'));
const CadPortalBootstrap = lazy(() => import('./pages/CadPortalBootstrap'));
const PidEditorPage = lazy(() => import('./pages/PidEditorPage'));
const PidPortalBootstrap = lazy(() => import('./pages/PidPortalBootstrap'));
const SldEditorPage = lazy(() => import('./pages/SldEditorPage'));
const SldPortalBootstrap = lazy(
  () => import('./pages/SingleLinePortalBootstrap')
);

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppRouteLoading() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="page-loader">
          <Spinner size="xl" pageLoader className="mx-auto" />
        </div>
        <p className="mt-4 text-ink-subtle">Loading…</p>
      </div>
    </div>
  );
}

function App() {
  // Initialize theme immediately (light/dark)
  useTheme();
  const spreadsheetEditorEnabled =
    import.meta.env.VITE_SPREADSHEET_EDITOR_ENABLED !== 'false';

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <TenantProvider>
            <div className="min-h-screen bg-background text-ink">
              <Suspense fallback={<AppRouteLoading />}>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/" element={<TenantPickerPage />} />
                  <Route path="/portal" element={<PortalPage />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/projects/new" element={<CreateProjectPage />} />
                  <Route
                    path="/projects/:projectId"
                    element={<ProjectDetail />}
                  />
                  {spreadsheetEditorEnabled && (
                    <>
                      <Route
                        path="/projects/:projectId/spreadsheets/:artifactId"
                        element={<SpreadsheetEditorPage />}
                      />
                      <Route
                        path="/projects/:projectId/spreadsheet-reviews/:reviewId/:artifactId"
                        element={<SpreadsheetEditorPage />}
                      />
                      <Route
                        path="/projects/:projectId/spreadsheet-plans/:planId"
                        element={<WorkbookPlanReviewPage />}
                      />
                      <Route
                        path="/spreadsheets/new"
                        element={<SpreadsheetPortalBootstrap />}
                      />
                      <Route
                        path="/spreadsheets/company/:documentId"
                        element={<SpreadsheetEditorPage />}
                      />
                    </>
                  )}
                  <Route path="/cad" element={<CadPortalBootstrap />} />
                  <Route
                    path="/projects/:projectId/cad/:artifactId"
                    element={<CadEditorPage />}
                  />
                  <Route path="/pid" element={<PidPortalBootstrap />} />
                  <Route path="/pid/scratch" element={<PidEditorPage />} />
                  <Route
                    path="/projects/:projectId/pid/:diagramId"
                    element={<PidEditorPage />}
                  />
                  <Route path="/single-line" element={<SldPortalBootstrap />} />
                  <Route
                    path="/single-line/scratch"
                    element={<SldEditorPage />}
                  />
                  <Route
                    path="/projects/:projectId/single-line/:diagramId"
                    element={<SldEditorPage />}
                  />
                  <Route
                    path="/fine-tune/:companyId"
                    element={<FineTuneDashboard />}
                  />
                  <Route path="/pm" element={<PMPage />} />
                  <Route
                    path="/standards-studio"
                    element={<StandardsStudio />}
                  />
                  <Route path="/chat" element={<ChatPage />} />
                  <Route
                    path="/company-documents"
                    element={<CompanyDocumentsPage />}
                  />
                  <Route
                    path="/projects/:projectId/documents/:artifactId"
                    element={<DocumentEditorPage />}
                  />
                  <Route
                    path="/documents/new"
                    element={<DocumentPortalBootstrap />}
                  />
                  <Route
                    path="/documents/company/:documentId"
                    element={<DocumentEditorPage />}
                  />
                  <Route path="/deliverables" element={<DeliverablesAdmin />} />
                  <Route path="/user-admin" element={<UserAdminPage />} />
                  <Route path="/operator" element={<OperatorConsoleLayout />}>
                    <Route index element={<OperatorConsoleHome />} />
                    <Route path="tenants" element={<PlatformTenantsPage />} />
                    <Route
                      path="organizations"
                      element={<OrganizationsAdminPage />}
                    />
                    <Route
                      path="model-context"
                      element={<ModelContextWindows />}
                    />
                    <Route path="users" element={<OperatorUserAdminPage />} />
                    <Route
                      path="environments"
                      element={<OperatorEphemeralEnvironmentsPage />}
                    />
                  </Route>
                  <Route
                    path="/model-context-windows"
                    element={<Navigate to="/operator/model-context" replace />}
                  />
                  <Route
                    path="/organizations"
                    element={<Navigate to="/operator/organizations" replace />}
                  />
                  <Route
                    path="/platform/tenants"
                    element={<Navigate to="/operator/tenants" replace />}
                  />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/beta-access" element={<BetaAccessPage />} />
                  {APP_TILES.filter((t) => t.comingSoon).map((t) => (
                    <Route
                      key={`beta-redirect-${t.id}`}
                      path={t.to}
                      element={
                        <Navigate
                          to={`/beta-access?tile=${encodeURIComponent(t.id)}`}
                          replace
                        />
                      }
                    />
                  ))}
                </Routes>
              </Suspense>
              <WorkbookReviewNotifications />
            </div>
          </TenantProvider>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Component to handle auth callback
function AuthCallback() {
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      localStorage.setItem('authToken', token);
      window.location.href = '/portal';
    } else {
      window.location.href = '/login';
    }
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="page-loader">
          <Spinner size="xl" pageLoader className="mx-auto" />
        </div>
        <p className="mt-4 text-ink-subtle">Completing authentication...</p>
      </div>
    </div>
  );
}

export default App;
